import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Grid,
  Avatar,
  Divider
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Security as SecurityIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';

interface Guest {
  id: string;
  email: string;
  fullName: string;
  phoneNumber?: string;
  oauthProvider: string;
  status: string;
  createdAt?: string;
  updatedAt?: string;
}

interface GuestProfileProps {
  guest: Guest;
  onRefresh: () => void;
}

export const GuestProfile: React.FC<GuestProfileProps> = ({ guest }) => {
  // Safety check for guest object
  if (!guest) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          Profile information not available
        </Typography>
      </Box>
    );
  }
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getProviderIcon = (provider: string) => {
    if (!provider) return '🔐';
    switch (provider.toLowerCase()) {
      case 'google':
        return '🔍';
      case 'amazon':
        return '📦';
      case 'yahoo':
        return '📧';
      default:
        return '🔐';
    }
  };

  const getProviderColor = (provider: string) => {
    if (!provider) return '#666';
    switch (provider.toLowerCase()) {
      case 'google':
        return '#4285f4';
      case 'amazon':
        return '#ff9900';
      case 'yahoo':
        return '#7b0099';
      default:
        return '#666';
    }
  };

  const getStatusColor = (status: string) => {
    if (!status) return 'default';
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  const getInitials = (name: string) => {
    if (!name) return 'G';
    return name
      .split(' ')
      .map(part => part && part.charAt(0).toUpperCase())
      .filter(Boolean)
      .join('')
      .slice(0, 2) || 'G';
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <PersonIcon sx={{ mr: 1 }} />
        My Profile
      </Typography>

      <Grid container spacing={3}>
        {/* Main Profile Card */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={3}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    fontSize: '2rem',
                    bgcolor: 'primary.main',
                    mr: 3
                  }}
                >
                  {getInitials(guest.fullName || 'Guest User')}
                </Avatar>
                <Box>
                  <Typography variant="h4" component="div" gutterBottom>
                    {guest.fullName || 'Guest User'}
                  </Typography>
                  <Chip
                    label={guest.status ? (guest.status.charAt(0).toUpperCase() + guest.status.slice(1)) : 'Unknown'}
                    color={getStatusColor(guest.status) as any}
                    size="small"
                  />
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <EmailIcon sx={{ mr: 2, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Email Address
                      </Typography>
                      <Typography variant="body1">
                        {guest.email || 'Not provided'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <PhoneIcon sx={{ mr: 2, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Phone Number
                      </Typography>
                      <Typography variant="body1">
                        {guest.phoneNumber || 'Not provided'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <SecurityIcon sx={{ mr: 2, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Authentication Provider
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <span style={{ marginRight: 8, fontSize: '1.2rem' }}>
                          {getProviderIcon(guest.oauthProvider || '')}
                        </span>
                        <Typography 
                          variant="body1"
                          sx={{ color: getProviderColor(guest.oauthProvider || '') }}
                        >
                          {guest.oauthProvider ? (guest.oauthProvider.charAt(0).toUpperCase() + guest.oauthProvider.slice(1)) : 'Unknown'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <PersonIcon sx={{ mr: 2, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Guest ID
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                        {guest.id || 'Not available'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Account Information Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <ScheduleIcon sx={{ mr: 1 }} />
                Account Information
              </Typography>

              <Box mb={3}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Account Created
                </Typography>
                <Typography variant="body1">
                  {formatDate(guest.createdAt)}
                </Typography>
              </Box>

              <Box mb={3}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Last Updated
                </Typography>
                <Typography variant="body1">
                  {formatDate(guest.updatedAt)}
                </Typography>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Typography variant="body2" color="text.secondary">
                Your profile information is used to personalize your experience and manage your event participation. 
                If you need to update any information, please contact the event organizer.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};